import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface ICreateBvnVerificationRequest {
  bvn: string;
  firstName: string;
  lastName: string;
  userPhotoUrl: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
}

interface ICreateBvnVerificationResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateBvnVerificationResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createBvnVerificationAsync = createAsyncThunk(
  'bvnVerification/create',
  async (
    values: ICreateBvnVerificationRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      };

      const { data } = await api.post(
        '/representative-bvn-verification',
        values,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateBvnVerificationResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const bvnVerificationCreationSlice = createSlice({
  name: 'bvnVerification/create',
  initialState,
  reducers: {
    resetBvnVerification: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBvnVerificationAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createBvnVerificationAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateBvnVerificationResponse;
      })
      .addCase(createBvnVerificationAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateBvnVerificationResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetBvnVerification } = bvnVerificationCreationSlice.actions;

export default bvnVerificationCreationSlice.reducer;
